// Environment detection and configuration
export interface EnvironmentConfig {
  apiUrl: string;
  hrApiUrl: string;
  environment: 'development' | 'staging' | 'production';
  isDevelopment: boolean;
  isStaging: boolean;
  isProduction: boolean;
}

// Detect environment based on hostname and other factors
const detectEnvironment = (): 'development' | 'staging' | 'production' => {
  const hostname = window.location.hostname;
  const port = window.location.port;

  // Check explicit environment variable first (highest priority)
  if (process.env.REACT_APP_ENV === 'production') {
    return 'production';
  }
  if (process.env.REACT_APP_ENV === 'staging') {
    return 'staging';
  }
  if (process.env.REACT_APP_ENV === 'development') {
    return 'development';
  }

  // Development environment
  if (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.startsWith('192.168.') ||
    hostname.startsWith('10.') ||
    port === '3000' ||
    process.env.NODE_ENV === 'development'
  ) {
    return 'development';
  }

  // Staging environment
  if (
    hostname.includes('staging') ||
    hostname === 'stagingtt.tekssolutions.com' ||
    hostname === '72.167.55.161' ||
    hostname.includes('stagingtt')
  ) {
    return 'staging';
  }

  // Production environment - check for production hostnames
  if (
    hostname === 'tekstime.tekssolutions.com' ||
    hostname === 'tekssolutions.com' ||
    hostname.includes('tekstime') && !hostname.includes('staging')
  ) {
    return 'production';
  }

  // Default to production for any other tekssolutions.com domain
  if (hostname.includes('tekssolutions.com') && !hostname.includes('staging')) {
    return 'production';
  }

  // Production environment (default fallback)
  return 'production';
};

// Get API URL based on environment
const getApiUrl = (environment: string): string => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  switch (environment) {
    case 'development': {
      const hostname = window.location.hostname;
      // When opened from another machine on the LAN (e.g. Windows pilot PC),
      // API must target the same host serving the frontend — not localhost.
      if (hostname && hostname !== 'localhost' && hostname !== '127.0.0.1') {
        return `http://${hostname}:3001/api`;
      }
      return 'http://localhost:3001/api';
    }
    case 'staging':
      // Same-origin HTTPS via IIS reverse proxy (web.config /api -> localhost:8081)
      return '/api';
    case 'production':
      return 'https://tekstime.tekssolutions.com/api'; // Production API URL
    default:
      return 'http://localhost:3001/api';
  }
};

// Get HR System API URL based on environment
const getHrApiUrl = (environment: string): string => {
  switch (environment) {
    case 'development':
      return 'http://localhost:61709/api'; // Local HR system
    case 'staging':
      return 'https://staginghr.tekssolutions.com/api';
    case 'production':
      return 'https://hr.tekssolutions.com/api'; // Production HR system API URL
    default:
      return 'http://localhost:61709/api';
  }
};

// Create environment configuration
export const createEnvironmentConfig = (): EnvironmentConfig => {
  const environment = detectEnvironment();
  const apiUrl = getApiUrl(environment);
  const hrApiUrl = getHrApiUrl(environment);

  return {
    apiUrl,
    hrApiUrl,
    environment,
    isDevelopment: environment === 'development',
    isStaging: environment === 'staging',
    isProduction: environment === 'production',
  };
};

// Export the configuration
export const envConfig = createEnvironmentConfig();

// Log environment info in development
if (envConfig.isDevelopment) {
  console.log('🌍 Environment Configuration:', {
    environment: envConfig.environment,
    apiUrl: envConfig.apiUrl,
    hostname: window.location.hostname,
    port: window.location.port,
  });
}

// Always log environment info for debugging
console.log('🌍 Frontend Environment Detection:', {
  environment: envConfig.environment,
  apiUrl: envConfig.apiUrl,
  hrApiUrl: envConfig.hrApiUrl,
  hostname: window.location.hostname,
  port: window.location.port,
  nodeEnv: process.env.NODE_ENV,
  reactAppEnv: process.env.REACT_APP_ENV,
});
